import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import { formatEvidenceName } from "../../../../common/entities/EntityUtils";
import useContributorQuery from "../../../../hooks/query/connections/useContributorQuery";
import {
  ContributorConnection,
  SYSTEM_CONTRIBUTION_TYPES_LABELS,
  SystemContributorTypes,
} from "../../../../types/contributor";
import { Version } from "../../../../types/version";

interface ContributorsViewSectionProps {
  anchor: Version;
  systemLanguageType: ContributorConnection["system_language_type"];
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const formatContributionType = (type: SystemContributorTypes | null): string =>
  (type && SYSTEM_CONTRIBUTION_TYPES_LABELS[type]) || "N/A";

const displayEvidenceName = (
  evidence:
    | ContributorConnection["name_internal_evidence"]
    | ContributorConnection["name_external_evidence"]
): string => {
  const formatted = formatEvidenceName(evidence);
  return formatted === "-" ? "N/A" : formatted;
};

const getContributorFields = (
  edge: ContributorConnection
): ViewAccordionField[] => [
  {
    label: "Contribution Type",
    value: (
      <Typography variant="body1">
        {formatContributionType(edge?.system_contribution_type ?? null)}
      </Typography>
    ),
  },
  {
    label: "Status",
    value: <Typography variant="body1">{displayText(edge?.status)}</Typography>,
  },
  {
    label: "Role",
    value: <Typography variant="body1">{displayText(edge?.role)}</Typography>,
  },
  {
    label: "Place",
    value: edge?.place ? (
      <EntityReferenceComponent entity={edge.place} linkMode="view" />
    ) : (
      <Typography variant="body1">N/A</Typography>
    ),
  },
  {
    label: "Place Identification Status",
    value: (
      <Typography variant="body1">
        {displayText(edge?.place_identification_status)}
      </Typography>
    ),
  },
  {
    label: "Name Internal Evidence",
    value: (
      <Typography variant="body1">
        {displayEvidenceName(edge?.name_internal_evidence)}
      </Typography>
    ),
  },
  {
    label: "Name External Evidence",
    value: (
      <Typography variant="body1">
        {displayEvidenceName(edge?.name_external_evidence)}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof edge?.notes === "string" ? edge.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const ContributorsViewSection: React.FC<ContributorsViewSectionProps> = ({
  anchor,
  systemLanguageType,
}) => {
  const { useFetchConnectedEdges } = useContributorQuery(
    SystemContributorTypes.TRANSLATOR,
    systemLanguageType
  );
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    anchor?._id,
    "outbound",
    {}
  );

  const items = useMemo<ContributorConnection[]>(
    () => data?.data ?? [],
    [data]
  );

  return (
    <ConnectionAccordionSection<ContributorConnection>
      title="Contributors"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(edge, index) => edge?._key ?? `contributor-${index}`}
      getItemTitle={(edge) =>
        edge?.toDoc ? (
          <EntityReferenceComponent entity={edge.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(edge) =>
        formatContributionType(edge?.system_contribution_type ?? null)
      }
      getItemFields={(edge) => getContributorFields(edge)}
      emptyText="No contributors"
    />
  );
};

export default ContributorsViewSection;
